import React, { useMemo } from "react";
import { AlertTriangle, UserPlus } from "lucide-react";

const DAY_MS = 24 * 60 * 60 * 1000;
const toDate = (iso) => new Date(`${iso}T00:00:00`);
const isoOf = (d) => d.toISOString().slice(0, 10);
const fmtShort = (d) => d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
const isoWeek = (d) => {
  const t = new Date(d.getTime());
  t.setHours(0, 0, 0, 0);
  t.setDate(t.getDate() + 3 - ((t.getDay() + 6) % 7));
  const week1 = new Date(t.getFullYear(), 0, 4);
  return 1 + Math.round(((t - week1) / DAY_MS - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

function weekStatus(pct) {
  if (pct >= 100) return { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", bar: "#EF4444" };
  if (pct >= 70) return { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", bar: "#F59E0B" };
  return { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200", bar: "#10B981" };
}

function phaseColor(label) {
  const l = (label || "").toLowerCase();
  if (l.includes("onboarding")) return "bg-purple-500";
  if (l.includes("offboarding")) return "bg-indigo-500";
  if (l.includes("profil")) return "bg-teal-600";
  if (l.includes("selbstvermarktung")) return "bg-sky-500";
  return "bg-slate-500";
}

function buildWeeks(count = 6) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const weeks = [];
  for (let i = 0; i < count; i++) {
    const start = new Date(monday);
    start.setDate(monday.getDate() + i * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 7);
    weeks.push({ start, end, kw: isoWeek(start) });
  }
  return weeks;
}

function assignLanes(entries, calStart, calEnd) {
  const ranged = entries
    .map((e) => ({ ...e, _s: Math.max(toDate(e.start_date), calStart), _e: Math.min(toDate(e.end_date), calEnd) }))
    .filter((e) => e._e > e._s)
    .sort((a, b) => a._s - b._s);
  const laneEnds = [];
  ranged.forEach((e) => {
    let lane = laneEnds.findIndex((end) => end <= e._s);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(e._e); } else { laneEnds[lane] = e._e; }
    e._lane = lane;
  });
  return { entries: ranged, laneCount: laneEnds.length || 1 };
}

const VAC_LABEL = { urlaub: "Urlaub", krank: "Krank", fortbildung: "Fortbildung", dienstreise: "Dienstreise", sonstiges: "Abwesend" };

function vacDaysInWeek(vacs, w) {
  let n = 0;
  const types = new Set();
  for (let i = 0; i < 5; i++) {
    const d = isoOf(new Date(w.start.getTime() + i * DAY_MS + 12 * 3600 * 1000));
    const v = (vacs || []).find((x) => x.start_date <= d && x.end_date >= d);
    if (v) { n += 1; types.add(VAC_LABEL[v.type] || "Urlaub"); }
  }
  return { days: n, label: [...types].join("/") || "Urlaub" };
}

function entriesFor(c) {
  const legacy = (c.clients || [])
    .filter((row) => !row.phase && row.hours_per_week != null)
    .map((row) => ({
      label: null, client_name: row.name, start_date: row.start_date || isoOf(new Date()),
      end_date: row.finish_date, hours_per_week: row.hours_per_week, hours_per_ue: row.hours_per_ue || 0.5,
    }));
  const segments = (c.all_segments || []).map((s) => ({
    label: s.label, client_name: s.client_name, start_date: s.start_date, end_date: s.end_date,
    hours_per_week: s.hours_per_week, hours_per_ue: s.hours_per_ue || 0.5,
  }));
  return [...legacy, ...segments];
}

export default function WeeklyCapacityCalendar({ coaches, canAssign, onAssignClick }) {
  const weeks = useMemo(() => buildWeeks(6), []);
  const calStart = weeks[0].start;
  const calEnd = weeks[weeks.length - 1].end;
  const totalMs = calEnd - calStart;

  return (
    <div data-testid="weekly-calendar-grid" className="space-y-4">
      <div className="grid gap-1" style={{ gridTemplateColumns: `220px repeat(${weeks.length}, 1fr)` }}>
        <div />
        {weeks.map((w) => (
          <div key={w.kw} data-testid={`week-header-${w.kw}`} className="text-center text-[10px] font-bold uppercase tracking-overline text-slate-500 pb-1">
            KW {w.kw}
            <div className="text-[9px] font-normal text-slate-400">{fmtShort(w.start)}–{fmtShort(new Date(w.end - DAY_MS))}</div>
          </div>
        ))}
      </div>

      {coaches.map((c) => {
        const entries = entriesFor(c);
        const { entries: laneEntries, laneCount: rawLanes } = assignLanes(entries, calStart, calEnd);
        const vacBars = (c.vacations || [])
          .map((v) => ({ ...v, _s: Math.max(toDate(v.start_date), calStart), _e: Math.min(new Date(toDate(v.end_date).getTime() + DAY_MS), calEnd) }))
          .filter((v) => v._e > v._s);
        const vacLane = vacBars.length ? 1 : 0;
        const laneCount = rawLanes + vacLane;
        const cap = c.weekly_capacity_hours || 20;
        const slotHours = 6;
        const overbooked = c.utilization_pct >= 100;
        return (
          <div key={c.user_id} className={`border border-slate-200 bg-white ${overbooked ? "capacity-overbooked" : ""}`} data-testid={`coach-capacity-card-${c.user_id}`} data-overbooked={overbooked}>
            <div className="grid gap-1 items-stretch" style={{ gridTemplateColumns: `220px repeat(${weeks.length}, 1fr)` }}>
              <div className="px-3 py-3 flex flex-col justify-center border-r border-slate-100">
                <div className="text-sm font-medium text-slate-900">{c.name}</div>
                <div className="text-[11px] text-slate-500" data-testid={`capacity-pct-${c.user_id}`}>{c.utilization_pct}% Gesamt-Auslastung</div>
              </div>
              {weeks.map((w) => {
                const wStartIso = isoOf(w.start);
                const wEndIso = isoOf(new Date(w.end - DAY_MS));
                let hours = 0;
                let ue = 0;
                entries.forEach((e) => {
                  if (!e.end_date) return;
                  if (e.start_date <= wEndIso && e.end_date >= wStartIso) {
                    hours += e.hours_per_week;
                    ue += e.hours_per_week / (e.hours_per_ue || 0.5);
                  }
                });
                ue = Math.round(ue * 10) / 10;
                const vac = vacDaysInWeek(c.vacations, w);
                const effCap = (cap * (5 - vac.days)) / 5;
                const pct = effCap > 0 ? Math.round((hours / effCap) * 100) : (hours > 0 ? 100 : 0);
                const meta = vac.days === 5 ? { border: "border-orange-200", bg: "vacation-hatch", text: "text-orange-800" } : weekStatus(pct);
                const freeSlots = Math.max(0, Math.floor((effCap - hours) / slotHours));
                return (
                  <div key={w.kw} data-testid={`coach-week-cell-${c.user_id}-${w.kw}`}
                    className={`text-center px-1 py-2 border ${meta.border} ${meta.bg}`}>
                    <div className={`text-xs font-mono font-semibold ${meta.text}`}>{hours.toFixed(1)}h</div>
                    <div className="text-[9px] text-slate-500">≈{ue} UE</div>
                    {vac.days === 5 ? (
                      <div className="text-[9px] mt-0.5 text-orange-700 font-bold" data-testid={`coach-week-vacation-${c.user_id}-${w.kw}`}>{vac.label} · ganze Woche</div>
                    ) : (
                      <div className={`text-[9px] mt-0.5 ${pct >= 100 ? "text-red-600 font-bold" : "text-slate-500"}`}>
                        {pct >= 100 ? "voll" : `+${freeSlots} frei`}
                      </div>
                    )}
                    {vac.days > 0 && vac.days < 5 && (
                      <div className="text-[9px] text-orange-700 font-medium" data-testid={`coach-week-vacation-${c.user_id}-${w.kw}`}>{vac.label} {vac.days} T · {effCap.toFixed(0)}h Kap.</div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="relative mx-3 mb-3 mt-2 bg-slate-50 border border-slate-100" style={{ height: `${Math.max(36, laneCount * 18 + 4)}px` }}>
              {vacBars.map((v) => {
                const left = ((v._s - calStart) / totalMs) * 100;
                const width = Math.max(1.5, ((v._e - v._s) / totalMs) * 100);
                return (
                  <div key={v.vacation_id} data-testid={`vacation-bar-${v.vacation_id}`}
                    title={`${VAC_LABEL[v.type] || "Urlaub"} ${v.start_date} – ${v.end_date}`}
                    className="absolute vacation-hatch border border-orange-300 text-orange-900 text-[9px] font-semibold leading-tight px-1 overflow-hidden whitespace-nowrap"
                    style={{ left: `${left}%`, width: `${width}%`, top: "2px", height: "16px" }}>
                    {VAC_LABEL[v.type] || "Urlaub"} {fmtShort(toDate(v.start_date))}–{fmtShort(toDate(v.end_date))}
                  </div>
                );
              })}
              {laneEntries.map((e, i) => {
                const left = ((e._s - calStart) / totalMs) * 100;
                const width = Math.max(1.5, ((e._e - e._s) / totalMs) * 100);
                return (
                  <div key={i} data-testid={`phase-bar-${e.client_name}-${e.label || "legacy"}`}
                    title={`${e.client_name}${e.label ? " – " + e.label : ""} · ${e.hours_per_week}h/Woche`}
                    className={`absolute ${phaseColor(e.label)} text-white text-[9px] leading-tight px-1 overflow-hidden whitespace-nowrap opacity-90 hover:opacity-100`}
                    style={{ left: `${left}%`, width: `${width}%`, top: `${(e._lane + vacLane) * 18 + 2}px`, height: "16px" }}>
                    {e.client_name}{e.label ? ` – ${e.label}` : ""}
                  </div>
                );
              })}
              {laneEntries.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-400 italic">Keine aktiven Kunden</div>
              )}
            </div>

            <div className="flex items-center justify-between px-3 pb-3">
              <div className="text-[11px] text-slate-500" data-testid={`capacity-free-now-${c.user_id}`}>
                {overbooked && <span className="inline-flex items-center gap-1 text-red-600 font-medium"><AlertTriangle size={11} /> Überbucht</span>}
                {!overbooked && (c.free_slots_now > 0
                  ? <>Jetzt frei für <b className="text-slate-900">{c.free_slots_now}</b> neue Kunden ({c.free_hours_now}h übrig)</>
                  : "Aktuell keine freie Kapazität")}
              </div>
              {canAssign && (
                <button onClick={() => onAssignClick(c)} data-testid={`capacity-assign-button-${c.user_id}`}
                  className="flex items-center gap-1.5 text-xs font-medium text-[#00807E] border border-[#00807E]/30 hover:bg-[#00807E]/5 px-3 py-1.5">
                  <UserPlus size={13} /> Kunde zuweisen
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
